import type { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { AutocompletePrediction } from '@bridebook/web/app-shared/lib/search/types';
import { isCustomAutocompletePrediction } from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions';
import { tryGetGooglePlaceFromFirestore } from '@bridebook/web/app-shared/lib/search/utils/get-google-place-from-firestore';
import { getArea } from '@bridebook/web/lib/search/utils';
import type { IDeps } from '@bridebook/web/lib/types';
import type { ILatLongBounds } from '@bridebook/web/lib/weddings/types';

export type TResolveSearchAreaRequest =
  | {
      type: 'autocomplete';
      text: string;
      countryCode?: CountryCodes;
    }
  | {
      type: 'prediction';
      prediction: AutocompletePrediction;
    };

export interface IResolveSearchAreaDeps
  extends Pick<IDeps, 'googlePlacesService' | 'autocompletePredictionsService'> {
  countryLatLongBounds: ILatLongBounds;
}

export interface IResolvedSearchArea {
  area: string;
  placeId: AutocompletePrediction['place_id'];
}

const getPredictionByText = async (
  autocompletePredictionsService: IDeps['autocompletePredictionsService'],
  searchText: string,
  {
    countryCode,
    countryLatLongBounds,
  }: { countryCode: CountryCodes | undefined; countryLatLongBounds: ILatLongBounds },
) => {
  const { predictions } = await autocompletePredictionsService.getAutocompletePredictions(
    searchText,
    { countryCode, bounds: countryLatLongBounds, category: '' },
  );
  return predictions[0] ?? null;
};

const getAreaFromPrediction = async (
  googlePlacesService: IDeps['googlePlacesService'],
  prediction: AutocompletePrediction,
) => {
  if (isCustomAutocompletePrediction(prediction)) {
    return prediction.area;
  }
  const cached = await tryGetGooglePlaceFromFirestore(prediction.place_id);
  if (cached) googlePlacesService.deleteSessionToken();
  const place = cached ?? (await googlePlacesService.getPlaceDetails([prediction]));
  return getArea(place);
};

/**
 * Resolves typed text or an autocomplete prediction into a search area slug.
 * Text resolves through its first autocomplete prediction; without a country code it searches globally.
 * Returns null when no prediction matches.
 */
export const resolveSearchArea = async (
  request: TResolveSearchAreaRequest,
  {
    googlePlacesService,
    autocompletePredictionsService,
    countryLatLongBounds,
  }: IResolveSearchAreaDeps,
): Promise<IResolvedSearchArea | null> => {
  const prediction =
    request.type === 'autocomplete'
      ? await getPredictionByText(autocompletePredictionsService, request.text, {
          countryCode: request.countryCode || undefined,
          countryLatLongBounds,
        })
      : request.prediction;

  if (!prediction) return null;

  const area = await getAreaFromPrediction(googlePlacesService, prediction);
  return { area, placeId: prediction.place_id };
};
